// src/components/Navbar.js
import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaUser, FaHeart, FaShoppingCart, FaBox, FaBars, FaTimes, FaSun, FaMoon } from 'react-icons/fa';
import AuthContext from '../context/AuthContext';
import CartContext from '../context/CartContext';
import CompanyInfoContext from '../context/CompanyInfoContext';
import ThemeContext from '../context/ThemeContext';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const { cart } = useContext(CartContext);
  const { companyName, companyLogo } = useContext(CompanyInfoContext);
  const { isDarkMode, toggleDarkMode } = useContext(ThemeContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/products', label: 'Shop' },
    { path: '/testimonials', label: 'Reviews' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav className="bg-white dark:bg-dark-card sticky top-0 z-50 border-b border-sand-200 dark:border-dark-border shadow-sm">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            {companyLogo ? (
              <img
                src={`${process.env.REACT_APP_API_URL}/api/images/${companyLogo}?w=200`}
                alt="Logo"
                width="48"
                height="48"
                className="h-10 w-10 lg:h-12 lg:w-12 object-contain rounded-xl"
              />
            ) : (
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center group-hover:shadow-glow-teal transition-shadow">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
            )}
            <div className="hidden sm:block">
              <span className="font-display font-bold text-xl lg:text-2xl text-slate-900 dark:text-white">
                {companyName}
              </span>
              <span className="block text-[10px] uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400 font-semibold -mt-0.5">
                Handcrafted Artistry
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30'
                    : 'text-slate-600 dark:text-slate-300 hover:text-teal-700 dark:hover:text-teal-400 hover:bg-sand-100 dark:hover:bg-dark-elevated'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-1 lg:space-x-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-sand-100 dark:hover:bg-dark-elevated transition-all"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <FaSun className="w-5 h-5 text-gold-400" />
              ) : (
                <FaMoon className="w-5 h-5" />
              )}
            </button>

            {/* Orders (logged in only) */}
            {user && (
              <Link
                to="/orders"
                className="hidden md:flex items-center gap-2 px-3 py-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-sand-100 dark:hover:bg-dark-elevated transition-all"
              >
                <FaBox className="w-5 h-5" />
                <span className="text-sm font-medium hidden xl:block">Orders</span>
              </Link>
            )}

            {/* Wishlist */}
            <Link
              to="/wishlist"
              className="p-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-sand-100 dark:hover:bg-dark-elevated transition-all"
            >
              <FaHeart className="w-5 h-5" />
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-sand-100 dark:hover:bg-dark-elevated transition-all"
            >
              <FaShoppingCart className="w-5 h-5" />
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-teal-600 dark:bg-teal-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-glow-teal">
                  {cart.length}
                </span>
              )}
            </Link>

            {/* User Menu */}
            <div className="relative group">
              <Link
                to={user ? '/account' : '/login'}
                className="p-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-sand-100 dark:hover:bg-dark-elevated transition-all flex items-center"
              >
                <FaUser className="w-5 h-5" />
              </Link>

              {/* User Dropdown */}
              {user && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-dark-card rounded-xl shadow-elevated border border-sand-200 dark:border-dark-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 overflow-hidden z-50">
                  <div className="p-4 bg-sand-50 dark:bg-dark-elevated border-b border-sand-200 dark:border-dark-border">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Signed in as</p>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate mt-0.5">{user.email}</p>
                  </div>
                  <div className="py-2">
                    <Link
                      to="/account"
                      className="block px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-sand-50 dark:hover:bg-dark-elevated hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    >
                      My Account
                    </Link>
                    <Link
                      to="/orders"
                      className="block px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-sand-50 dark:hover:bg-dark-elevated hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    >
                      My Orders
                    </Link>
                    <Link
                      to="/wishlist"
                      className="block px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-sand-50 dark:hover:bg-dark-elevated hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    >
                      My Wishlist
                    </Link>
                    <hr className="my-2 border-sand-200 dark:border-dark-border" />
                    <button
                      onClick={logout}
                      className="block w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-sand-100 dark:hover:bg-dark-elevated transition-all"
            >
              {mobileMenuOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-sand-200 dark:border-dark-border py-4 animate-slide-down">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-xl font-medium transition-all ${
                    isActive(link.path)
                      ? 'text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30'
                      : 'text-slate-600 dark:text-slate-300 hover:text-teal-700 dark:hover:text-teal-400 hover:bg-sand-50 dark:hover:bg-dark-elevated'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user && (
                <>
                  <Link
                    to="/orders"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-sand-50 dark:hover:bg-dark-elevated rounded-xl transition-colors"
                  >
                    <FaBox className="w-5 h-5" />
                    <span>My Orders</span>
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 w-full text-left px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl font-medium transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
